import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import ScrollToTop from './components/ScrollToTop';
import { OnboardingErrorBoundary } from './components/OnboardingErrorBoundary';
import Layout from './components/Layout';
import RequireOnboarding from './components/RequireOnboarding';
import Loading from './components/Loading';
import reportWebVitals from './reportWebVitals';
import mixpanel from 'mixpanel-browser';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { init as initFullStory } from '@fullstory/browser';
import { UserProvider, CompanyProvider, UIProvider } from './contexts';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
// import { PostHogProvider} from 'posthog-js/react';
import './styles/Variables.css';
import './styles/index.css';

// Critical pages - keep synchronous for fast initial load (above-the-fold)
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ErrorPage from './pages/ErrorPage';
import Leads from './pages/Leads';

// Lazy load all other pages for code splitting
const VerifyProperty = lazy(() => import('./pages/VerifyProperty'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Settings = lazy(() => import('./pages/Settings'));
const Preferences = lazy(() => import('./pages/Preferences'));
const ReportLoading = lazy(() => import('./pages/ReportLoading'));
const GodMode = lazy(() => import('./pages/GodMode'));
const Reports = lazy(() => import('./pages/Reports'));
const Automations = lazy(() => import('./pages/Automations'));
const GPTWrapper = lazy(() => import('./pages/GPTWrapper'));
const Profile = lazy(() => import('./pages/Profile'));
const TestView = lazy(() => import('./pages/TestView'));
const XIntegration = lazy(() => import('./pages/XIntegration'));
const XOAuthCallback = lazy(() => import('./pages/XOAuthCallback'));
const SalesforceCallback = lazy(() => import('./pages/SalesforceCallback'));
const FUBCallback = lazy(() => import('./pages/FUBCallback'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const RunComps = lazy(() => import('./pages/RunComps')); // 2543 lines - biggest win!
const RunCompsV2 = lazy(() => import('./pages/RunCompsV2'));
const ShareComps = lazy(() => import('./pages/ShareComps'));
const EnterCompanyName = lazy(() => import('./pages/EnterCompanyName'));
const OnboardingFlow = lazy(() => import('./pages/OnboardingFlow'));
const Welcome = lazy(() => import('./pages/Welcome'));
const PostConversionSetup = lazy(() => import('./pages/PostConversionSetup'));
const StripeLink = lazy(() => import('./pages/StripeLink'));
const ImageGPTWrapper = lazy(() => import('./pages/ImageGPTWrapper'));
const GHLSetup = lazy(() => import('./pages/GHLSetup'));
const CompTracking = lazy(() => import('./pages/CompTracking'));
const LeadDetailV2 = lazy(() => import('./pages/LeadDetailV2'));
const Buyers = lazy(() => import('./pages/Buyers'));
const BuyerDetail = lazy(() => import('./pages/BuyerDetail'));
const BuyerSignup = lazy(() => import('./pages/BuyerSignup'));
const BuyerSignupPrivacy = lazy(() => import('./pages/BuyerSignupPrivacy'));
const DealFeed = lazy(() => import('./pages/DealFeed'));
const DealPresentation = lazy(() => import('./pages/DealPresentation'));
const LeadBuyerMatches = lazy(() => import('./pages/LeadBuyerMatches'));
const BuyerSendDeals = lazy(() => import('./pages/BuyerSendDeals'));
const Deals = lazy(() => import('./pages/Deals'));
const DealDetail = lazy(() => import('./pages/DealDetail'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const Claim = lazy(() => import('./pages/Claim'));
const EvalDashboard = lazy(() => import('./pages/EvalDashboard/EvalDashboard'));
const Integrations = lazy(() => import('./pages/Integrations'));
const ConnectAI = lazy(() => import('./pages/ConnectAI'));
const ConnectClaude = lazy(() => import('./pages/ConnectClaude'));
const AiReturn = lazy(() => import('./pages/AiReturn'));
const SpencersDeals = lazy(() => import('./pages/SpencersDeals'));
const SpencersDealDetail = lazy(() => import('./pages/SpencersDealDetail'));
const TrialDeals = lazy(() => import('./pages/TrialDeals'));
const TrialDealDetail = lazy(() => import('./pages/TrialDealDetail'));
const TrialBuyBoxSetup = lazy(() => import('./pages/TrialBuyBoxSetup'));
const CoLivingScreen = lazy(() => import('./pages/CoLivingScreen'));

// Legacy /leads URL → / (Leads is now the home page). Preserve search params
// so links like /leads?archived=1 still land filter-applied at /.
function LeadsToHomeRedirect() {
    const { search } = useLocation();
    return <Navigate to={{ pathname: '/', search }} replace />;
}

// "ResizeObserver loop completed with undelivered notifications" is a benign
// browser notice — fired when a ResizeObserver callback couldn't settle layout
// within one frame. Google Maps' internal observer trips it when its container
// is laid out during the Leads map/table view toggle. It has no runtime effect,
// but CRA's react-error-overlay catches it via window 'error' and shows a fatal
// red screen in dev. Swallow just this message so it never reaches the overlay.
if (typeof window !== 'undefined') {
	window.addEventListener('error', (e) => {
		if (
			e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
			e.message === 'ResizeObserver loop limit exceeded'
		) {
			e.stopImmediatePropagation();
			e.preventDefault();
		}
	});
}

initFullStory({ orgId: JSON.parse(process.env.REACT_APP_FULLSTORY).orgId });

// const options = {
// 	api_host: JSON.parse(process.env.REACT_APP_POSTHOG).host
// };

// Initialize Firebase
const app = initializeApp(JSON.parse(process.env.REACT_APP_FIREBASE).config);
getAnalytics(app);

// Session replay is prod-only. On localhost it loads a cross-domain iframe
// from https://clozers.firebaseapp.com which the browser blocks because the
// dev server is http (protocol mismatch in Same-Origin Policy), spamming the
// console on every page load. And recording dev sessions is just noise —
// you're testing, not exercising real user flows.
const isLocalDev =
	typeof window !== 'undefined' &&
	(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

mixpanel.init(JSON.parse(process.env.REACT_APP_MIXPANEL).key,
	isLocalDev
		? { debug: false }
		: {
			debug: false,
			record_sessions_percent: 100,
			record_collect_fonts: true,
			record_mask_text_selector: ".mask-text",
			record_mask_text_class: ".mask-text",
			record_block_selector: ".mask-text"
		}
);

const routing = (
	<BrowserRouter>
		{/* <PostHogProvider
			apiKey={JSON.parse(process.env.REACT_APP_POSTHOG).key}
			options={options}
		> */}
		<QueryClientProvider client={queryClient}>
			<UserProvider>
				<CompanyProvider>
					<UIProvider>
						<ScrollToTop>
						<Suspense fallback={<Loading />}>
							<Routes>
								<Route path="/buyer-signup/:companySlug/privacy" element={<BuyerSignupPrivacy />} />
							<Route path="/buyer-signup/:companySlug" element={<BuyerSignup />} />
								<Route path="/deals/:companySlug/feed/:buyerId" element={<DealFeed />} />
								<Route path="/deals/:companySlug/feed" element={<DealFeed />} />
								<Route path="/deals/:companySlug/:leadId/:buyerId" element={<DealPresentation />} />
								<Route path="/deals/:companySlug/:leadId" element={<DealPresentation />} />
								<Route path="/spencers-list" element={<SpencersDeals />} />
									<Route path="/spencers-list/:dealId" element={<SpencersDealDetail />} />
								{/* Retired: the public co-living map and its deal pages. Both
								    now land on the leads home page. */}
								<Route path="/co-living-map" element={<Navigate to="/" replace />} />
									<Route path="/co-living-map/:dealId" element={<Navigate to="/" replace />} />
								<Route path="/my-deals/:trialId" element={<TrialDeals />} />
									<Route path="/my-deals/:trialId/buy-box" element={<TrialBuyBoxSetup />} />
										<Route path="/my-deals/:trialId/:dealId" element={<TrialDealDetail />} />
								{/* Public hand-back page for the MCP onboarding browser steps (inbox
								    connect + payment). Outside the auth guard so it loads even if the
								    user isn't signed into the web app in this browser. */}
								<Route path="/ai-return/:step" element={<AiReturn />} />
								{/* Public "set up Clozers in Claude" connect card — the single
								    shareable onboarding link. Outside the auth guard: a brand-new
								    user with no account lands here and signs up inside Claude's
								    OAuth flow. */}
								<Route path="/claude" element={<ConnectClaude />} />
								<Route element={<RequireOnboarding><Layout /></RequireOnboarding>}>
									<Route path="/" element={<Leads />} />
									<Route path="/sign-up" element={<SignUp />} />
									<Route path="/login" element={<Login />} />
									<Route path="/verify-property" element={<VerifyProperty />} />
									<Route path="/welcome" element={<Welcome />} />
								<Route path="/get-started" element={<PostConversionSetup />} />
									<Route path="/company-setup" element={<EnterCompanyName />} />
									<Route path="/onboarding/*" element={<OnboardingErrorBoundary><OnboardingFlow /></OnboardingErrorBoundary>} />
									<Route path="/privacy-policy" element={<PrivacyPolicy />} />
									<Route path="/terms-conditions" element={<TermsAndConditions />} />
									<Route path="/settings" element={<Settings />} />
									<Route path="/preferences" element={<Preferences />} />
									<Route path="/report-loading" element={<ReportLoading />} />
									<Route path="/demos/:countyId/:id" element={<RunComps />} />
									<Route path="/run-comps/:countyId/:id" element={<RunComps />} />
									<Route path="/run-comps-v2/:countyId/:id" element={<RunCompsV2 />} />
									<Route path="/run-comps-test/:countyId/:id" element={<TestView />} />
									<Route path="/sample-report" element={<RunComps />} />
									{/* <Route path="/missing-comps" element={<MissingComps />} /> */}
									<Route path="/reports" element={<Reports />} />
									<Route path="/leads" element={<LeadsToHomeRedirect />} />
								<Route path="/leads/:propertyId" element={<LeadDetailV2 />} />
								<Route path="/leads/:propertyId/buyers" element={<LeadBuyerMatches />} />
								<Route path="/buyers" element={<Buyers />} />
								<Route path="/buyers/:buyerId" element={<BuyerDetail />} />
							<Route path="/buyers/:buyerId/send-deals" element={<BuyerSendDeals />} />
									<Route path="/deals" element={<Deals />} />
									<Route path="/deals/:leadId" element={<DealDetail />} />
									<Route path="/automation" element={<Automations />} />
									<Route path="/leaderboard" element={<Leaderboard />} />
									{/* Internal review of the co-living screening batches
									    (Riverside + Orange County). Reads `oc_screen_deals`, NOT
									    the public co_living_deals feed: that one carries deals
									    someone chose to publish, and this page exists to show the
									    ones that FAILED and why. /orange-county kept as a redirect
									    — the batches outgrew the name once Riverside dominated. */}
									<Route path="/co-living-screen" element={<CoLivingScreen />} />
									<Route path="/orange-county" element={<Navigate to="/co-living-screen" replace />} />
									<Route path="/pricing" element={<Pricing />} />
									{/* <Route path="/text-labelling" element={<TextLabelling />} /> */}
									{/* <Route path="/image-labelling" element={<ImageLabelling />} /> */}
									<Route path="/comps/:companyId/:countyId/:id" element={<ShareComps />} />
									<Route path="/profile" element={<Profile />} />
									<Route path="/god-mode" element={<GodMode />} />
								<Route path="/eval" element={<EvalDashboard />} />
								<Route path="/eval/:runId" element={<EvalDashboard />} />
								<Route path="/eval/:runId/:caseId" element={<EvalDashboard />} />
									<Route path="/comp-tracking/:companyId/:propertyId" element={<CompTracking />} />
									<Route path="/stripe-link" element={<StripeLink />} />
									<Route path="/gpt-wrapper" element={<GPTWrapper />} />
									<Route path="/gpt-wrapper-image" element={<ImageGPTWrapper />} />
									<Route path="/crm-setup" element={<GHLSetup />} />
									<Route path="/x-integration" element={<XIntegration />} />
									<Route path="/integrations" element={<Integrations />} />
									<Route path="/connect-ai" element={<ConnectAI />} />
									<Route path="/oauth/x/callback" element={<XOAuthCallback />} />
									<Route path="/salesforcecallback" element={<SalesforceCallback />} />
									<Route path="/fubcallback" element={<FUBCallback />} />
									<Route path="/accept-invite" element={<AcceptInvite />} />
									<Route path="/claim" element={<Claim />} />
									{/* <Route path="/development" element={<Development />} /> */}
									<Route path="*" element={<ErrorPage />} />
								</Route>
							</Routes>
						</Suspense>
					</ScrollToTop>
					</UIProvider>
				</CompanyProvider>
			</UserProvider>
		</QueryClientProvider>
		{/* </PostHogProvider> */}
	</BrowserRouter>
);

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
	  <React.StrictMode>
		  {routing}
	  </React.StrictMode>
);

reportWebVitals();
